import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router-dom";
import { IMG_DAINASOUL_WALLPAPER } from "./imageConfig";

const NavDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [view, setView] = useState("main"); // 'main', 'ready-to-wear', 'accessories'

  const handleLinkClick = (path) => {
    navigate(path);
    onClose();
    setView("main"); // Reset on close
  };

  const navItems = [
    { name: "All Products", path: "/all-products", hasArrow: false },
    { name: "Ready to Wear", action: () => setView("ready-to-wear"), hasArrow: true },
    { name: "Accessories", action: () => setView("accessories"), hasArrow: true },
    { name: "Edits", action: () => setView("edits"), hasArrow: true },
  ];

  const subMenus = {
    "ready-to-wear": {
      title: "Ready to Wear",
      path: "/products/ready-to-wear",
      image: IMG_DAINASOUL_WALLPAPER,
      links: [
        { name: "SWEATSHIRTS", path: "/products/sweatshirts" },
        { name: "HOODIES", path: "/products/hoodies" },
        { name: "SHORTS", path: "/products/shorts" },
        { name: "T-SHIRTS & TOPS", path: "/products/t-shirts" },
      ]
    },
    "accessories": {
      title: "Accessories",
      path: "/products/accessories",
      image: IMG_DAINASOUL_WALLPAPER,
      links: [
        { name: "BAGS", path: "/products/bags" },
        { name: "CAPS", path: "/products/caps" },
      ]
    },
    "edits": {
      title: "Edits",
      path: "/all-products",
      image: IMG_DAINASOUL_WALLPAPER,
      links: [
        { name: "FALL ESSENTIALS", path: "/products/fall-essentials" },
        { name: "MATCHING SETS", path: "/products/matching-sets" },
        { name: "TONAL CLASSICS", path: "/products/tonal-classics" },
      ]
    }
  };

  const renderMainView = () => (
    <Box sx={{ p: 0 }}>
      {/* Header inside drawer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 500,
            letterSpacing: "0.05rem",
            cursor: "pointer"
          }}
          onClick={() => handleLinkClick("/")}
        >
          DAINASOUL
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#000" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Main Links */}
      <List sx={{ p: 0 }}>
        {navItems.map((item) => (
          <ListItem
            key={item.name}
            button
            onClick={() => item.action ? item.action() : handleLinkClick(item.path)}
            sx={{
              py: 2.5,
              px: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                sx: {
                  fontSize: "18px",
                  fontWeight: 500,
                },
              }}
            />
            {item.hasArrow && <ChevronRightIcon sx={{ color: "#888", fontSize: "20px" }} />}
          </ListItem>
        ))}
        <ListItem
          button
          onClick={() => handleLinkClick("/all-products")}
          sx={{ py: 2.5, px: 2 }}
        >
          <ListItemText
            primary="Last Chance"
            primaryTypographyProps={{
              sx: {
                fontSize: "18px",
                fontWeight: 600,
                color: "#ff4d4d",
              },
            }}
          />
        </ListItem>
      </List>

      {/* Auth Button */}
      <Box sx={{ mt: 4, mb: 4, px: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleLinkClick("/login")}
          sx={{
            py: 2,
            color: "#000",
            borderColor: "#000",
            borderRadius: 0,
            fontSize: "14px",
            fontWeight: 700,
            letterSpacing: "1px",
            "&:hover": {
              borderColor: "#000",
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          SIGN IN / REGISTER
        </Button>
      </Box>

      {/* Footer Links */}
      <Box sx={{ mt: 4, px: 2 }}>
        {[
          { text: "Frequently Asked Questions", path: "/faq" },
          { text: "Payment & Shipping", path: "/return-policy" },
          { text: "Sustainability", path: "/sustainability" },
        ].map((item) => (
          <Typography
            key={item.text}
            sx={{
              fontSize: "14px",
              mb: 2.5,
              cursor: "pointer",
              color: "#000",
            }}
            onClick={() => handleLinkClick(item.path)}
          >
            {item.text}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  const renderSubView = (menuKey) => {
    const menu = subMenus[menuKey];
    if (!menu) return null;

    return (
      <Box sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Sub-menu Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
          }}
        >
          <Button
            startIcon={<ArrowBackIosNewIcon sx={{ fontSize: "14px !important", color: "grey" }} />}
            onClick={() => setView("main")}
            sx={{
              color: "grey",
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "1px",
              textTransform: "uppercase",
              mixBlendMode: "difference",
              minWidth: 0,
              px: 0,
            }}
          >
            BACK
          </Button>
          <IconButton onClick={onClose} sx={{ color: "grey", mixBlendMode: "difference" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Promotional Header Image */}
        <Box
          sx={{
            width: "100%",
            height: "300px",
            backgroundImage: `url(${menu.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        <Box sx={{ p: 0 }}>
          <Typography
            variant="h5"
            onClick={() => handleLinkClick(menu.path)}
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
              mb: 4,
              mt: 4,
              px: 2,
              cursor: "pointer",
              "&:hover": { color: "#666" }
            }}
          >
            {menu.title}
          </Typography>

          <List sx={{ p: 0 }}>
            {menu.links.map((item) => (
              <ListItem
                key={item.name}
                button
                onClick={() => handleLinkClick(item.path)}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: "14px",
                      fontWeight: 600,
                      letterSpacing: "1px",
                      color: "#333",
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    );
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={() => {
        onClose();
        setTimeout(() => setView("main"), 300); // Small delay to avoid flickering during close animation
      }}
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: { 
          width: { xs: "85%", sm: "400px" }, 
          maxWidth: "100%", 
          height: "100%" 
        },
      }}
    >
      {view === "main" ? renderMainView() : renderSubView(view)}
    </Drawer>
  );
};

export default NavDrawer;
